import { Component, OnInit } from '@angular/core';
import { DialogBase } from '@routes/dialogs/dialog.base';
import Utils from '@shared/utils/utils';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Code } from '@shared/models/code';
import { MasterCode } from '@shared/enums/master-code';
import { DatePipe } from '@angular/common';
import Role from '@shared/constants/role';
import { Constants } from '@shared/constants/constants';
import { Company } from '@shared/enums/company';
import Roles from '@shared/constants/roles';
import Swal from 'sweetalert2';
import { finalize } from 'rxjs/operators';
import { forkJoin } from 'rxjs';


@Component({
  selector: 'app-user-edit',
  templateUrl: './user-edit.component.html',
  styleUrls: ['./user-edit.component.scss']
})
export class UserEditComponent extends DialogBase implements OnInit {

  title: any;
  saveButtonLabel: any;
  id: any;
  userInfo: any;
  simpleEdit: boolean;

  detailForm: FormGroup;
  groupOptions: Code[] = [];
  statusGroupOptions: Code[] = [];
  userTypeGroupOptions: Code[] = [];
  organizationGroupOptions: Code[] = [];
  storeGroupOptions: Code[] = [];
  companies: Code[] = [];
  dealers: Code[] = [];
  avoidMfaOptions: Code[] = [
    { code: true, name: 'Yes' },
    { code: false, name: 'No' }
  ];

  userType = null;
  returnedCompany: string = null;
  roles: Code[] = [];
  error: any;

  pipe = new DatePipe('en-US');
  company;
  isSysAdmin;
  isDealer;
  isAdmin;
  isAdminSelect: boolean = false;
  isLoggedInUserAdminSelect: boolean = false;
  isUserLocked: boolean = false;
  loginCompanies = Company;
  isOwnProfile: boolean = false;
  showPasswordFields: boolean = false;
  passwordForm: FormGroup;


  get form() {
    return this.detailForm.controls;
  }

  get passwordFormControls() {
    return this.passwordForm?.controls;
  }

  ngOnInit(): void {
    this.company = this.authService.getCurrentCompany();
    this.isSysAdmin = this.authService.getRoles().filter(roles => roles.groups.includes(Role.sysAdmin))?.length > 0;
    this.isAdmin = this.authService.getRoles().filter(roles => roles.groups.includes(Role.admin))?.length > 0;
    this.isDealer = this.authService.hasType(Roles.dealer);
    this.isLoggedInUserAdminSelect = this.authService.hasRoleInCompany(Role.adminSelect);

    // Check if user is editing their own profile
    const currentUser = this.authService.getUser();
    if (this.userInfo && currentUser.username === this.userInfo.username) {
      this.isOwnProfile = true;
    }

    this.setFormControls();
    this.setPasswordFormControls();
    this.setEmployeeNoField();
    this.setSelectOptions();

    this.form['roles'].valueChanges
      .subscribe(res => {
        if (res) {
          this.isAdminSelect = !!res.find(role => role === Role.adminSelect);
        } else {
          this.isAdminSelect = false
        }
      });
    if (this.userInfo?.lockUntil) {
      this.isUserLocked = true;
    }
  }

  selectUserType(event) {
    /** Clean Organization Input */
    const form = this.detailForm.controls;
    form['organization'].setValue(null);
    form['roles'].setValue(null);

    if (!event) {
      this.groupOptions = [];
      this.organizationGroupOptions = [];
      return;
    }

    this.userType = event.code;
    this.setRolesByUserType();
    this.setEmployeeNoField();
  }

  setPasswordFormControls() {
    this.passwordForm = this.fb.group({
      newPassword: [null, Validators.compose([Validators.required, Validators.minLength(8)])],
      confirmPassword: [null, Validators.required]
    }, {
      validators: this.passwordMatchValidator
    });
  }

  passwordMatchValidator(formGroup: FormGroup) {
    const newPassword = formGroup.get('newPassword')?.value;
    const confirmPassword = formGroup.get('confirmPassword')?.value;

    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      formGroup.get('confirmPassword')?.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }
    return null;
  }

  setFormControls() {

    let defaultControls: any = {
      firstName: [null, Validators.required],
      lastName: [null, Validators.required],
      username: [null, Validators.compose([Validators.required, Validators.email])],
      roles: [null, Validators.required], // email: [null, Validators.required],
      ext: null,
      createdAt: new FormControl({ value: ' ', disabled: true }),
      updatedAt: new FormControl({ value: ' ', disabled: true }),
      userType: [null, Validators.required],
      status: [null, Validators.required],
      organization: [null, Validators.required],
      employeeNo: new FormControl({ value: null, disabled: true }),
      optOut: null,
      doNotAllowComment: null,
      avoidMfa: [null],
    }
    const idControl = {
      _id: [null],
    }
    const telRequiredControl = {
      tel: [null, Validators.compose([Validators.required, Validators.pattern(/^([2-9]{1}[0-9]{2}[2-9]{1}[0-9]{6})$/)])],
    }
    const telOptionalControl = {
      tel: [null, Validators.pattern(/^([2-9]{1}[0-9]{2}[2-9]{1}[0-9]{6})$/)],
    }
    const hqStoresOptionControl = {
      hqStores: [null]
    }

    if (this.userInfo) {
      defaultControls = {
        ...defaultControls,
        ...idControl,
        ...hqStoresOptionControl,
      }
    }
    if ([this.loginCompanies.SeaIm].includes(this.company)) {
      defaultControls = {
        ...defaultControls,
        ...telOptionalControl,
      }
    } else {
      defaultControls = {
        ...defaultControls,
        ...telOptionalControl,
        ...hqStoresOptionControl,
      }
    }
    this.detailForm = this.fb.group(defaultControls);
  }

  setSelectOptions() {
    this.apiService.master.getConstant(MasterCode.UserTypes).subscribe(res => {

      let arr = res.filter(re => re.company.includes(this.company));
      if (this.isDealer || this.isLoggedInUserAdminSelect) {
        arr = res.filter(re => re.code === Role.dealer);
      }
      this.userTypeGroupOptions = arr;
    });

    this.apiService.master.getConstant(MasterCode.UserStatus).subscribe(res => {
      let arr = [];
      Object.keys(res).map(function (key) {
        arr.push({ ['code']: res[key], ['name']: res[key] })
        return arr;
      });
      this.statusGroupOptions = arr;
    });

    this.apiService.master.getConstant(MasterCode.Roles).subscribe(res => {
      this.roles = res;
      const getDealerCodes = this.apiService.dealer.getDealerCodes();
      const getCompanyCodes = this.apiService.company.getCompanyCodes();
      forkJoin([getDealerCodes, getCompanyCodes])
        .subscribe(([dealerCodes, companyCodes]) => {
          this.dealers = dealerCodes;
          this.companies = companyCodes;

          // Initialize user data after dealers and companies are loaded
          if (this.userInfo) {
            this.title = 'Edit';
            this.saveButtonLabel = 'Update';
            this.id = this.userInfo.id;
            this.userType = this.userInfo.userType;
            this.isAdminSelect = !!this.userInfo.roles?.find(role => role.company === Company.Sea)?.groups?.includes(Role.adminSelect);

            this.setEdit(this.userInfo);
            this.setRolesByUserType();
            this.setEmployeeNoField();
          }
        });
    });

    this.storeGroupOptions = this.authService.getHQDealers();
  }

  setRolesByUserType() {
    this.groupOptions = [];
    this.organizationGroupOptions = this.companies.filter(c => !c.be && !c.military);
    const roles: any = [];

    switch (this.company) {
      case Company.Sea:
        if (this.userType === Constants.userType.admin) {
          roles.push(Role.admin);
          roles.push(Role.cdAdmin);
          roles.push(Role.adminViewer);
          roles.push(Role.adminSelect);
          if (this.isSysAdmin) {
            roles.push(Role.sysAdmin);
            roles.push(Role.bizAdmin);
          }
        } else if (this.userType === Constants.userType.agent) {
          roles.push(Role.dealerAgent);
          roles.push(Role.concealedDamageAgent);
        } else if (this.userType === Constants.userType.dealer) {
          roles.push(Role.dealer);
          roles.push(Role.concealedDamageStore);
          roles.push(Role.dealerManager);
          this.organizationGroupOptions = this.dealers;
        } else if (this.userType === Constants.userType.dealerManager) {
          roles.push(Role.dealerManager)
          this.organizationGroupOptions = this.dealers;
        } else if (this.userType === Constants.userType.be) {
          roles.push(Role.be);
          this.organizationGroupOptions = this.companies.filter(c => c.be);
        } else if (this.userType === Constants.userType.beAdmin) {
          roles.push(Role.beAdmin);
          this.organizationGroupOptions = this.companies.filter(c => c.be);
        } else if (this.userType === Constants.userType.ra) {
          roles.push(Role.ra);
          this.organizationGroupOptions = this.companies.filter(c => c.military);
        } else if (this.userType === Constants.userType.raAdmin) {
          roles.push(Role.raAdmin);
          this.organizationGroupOptions = this.companies.filter(c => c.military);
        }
        break;
      case Company.SeaIm:
        if (this.userType === Constants.userType.admin) {
          roles.push(Role.seaImAdmin)
        } else if (this.userType === Constants.userType.agent) {
          roles.push(Role.seaImAgent);
        } else if (this.userType === Constants.userType.dealer) {
          roles.push(Role.seaImDealerSuperVisor);
          this.organizationGroupOptions = this.dealers;
        }
        break;
      case Company.Seca:
        if (this.userType === Constants.userType.admin) {
          roles.push(Role.secaAdmin)
        } else if (this.userType === Constants.userType.agent) {
          roles.push(Role.secaAgent);
        } else if (this.userType === Constants.userType.dealer) {
          roles.push(Role.secaDealerStaff);
          roles.push(Role.secaDealerSuperVisor);
          this.organizationGroupOptions = this.dealers;
        }
        break;
    }
    const result = this.roles.filter(role => roles.includes(role.code) && role.company.includes(this.company));
    this.groupOptions = result;
  }

  setEmployeeNoField() {
    // SECA has no GCIC contact-creation interface, so its BPNO has to be keyed in by hand.
    // SEA / SEA-IM stay read-only: there the value is returned by the BP interface on save.
    if ([this.loginCompanies.Seca].includes(this.company)) {
      this.form['employeeNo'].enable();
    } else {
      this.form['employeeNo'].disable();
    }
  }

  async submitForm($event) {
    $event.stopPropagation();
    $event.preventDefault();

    const currentCompany = this.authService.getCurrentCompany();

    let userData = this.detailForm.getRawValue();
    const rolesValue = userData.roles;

    if (typeof userData.employeeNo === 'string') {
      userData.employeeNo = userData.employeeNo.trim() || null;
    }

    userData.roles = this.returnedCompany
      ? [{
          company: this.returnedCompany,
          groups: rolesValue
        }]
      : [{ company: currentCompany, groups: rolesValue }];

    userData.email = userData.username;
    delete userData.groups;

    if (this.detailForm.invalid) {
      this.detailForm.markAllAsTouched();
      return;
    }

    this.spinnerService.start();
    if (userData.userType === Role.dealer) {
      if ([Company.Sea].includes(this.company)) {
        const contactData = {
          company: userData.company,
          bpNo: userData.organization,
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.username,
          tel: userData.tel,
        }
        userData.employeeNo = await this.bpService.saveContactBp(contactData, [], userData.company);
      } else if ([Company.Seca].includes(this.company)) {
        // SECA admins key the BPNO in themselves; a blank field keeps the old
        // dealer-BPNO fallback so existing users are not left without a value
        userData.employeeNo = userData.employeeNo || userData.organization;
      } else {
        userData.employeeNo = userData.organization;
      }
    }
    if ([Company.Sea].includes(this.company)) {
      userData.hqStores = this.form['hqStores'].value || null;
      if (!this.isAdminSelect) {
        userData.hqStores = null;
      }
    }
    this.apiService.user.saveUser(userData)
      .pipe(finalize(() => this.spinnerService.stop()))
      .subscribe(res => {
        const messages = !this.userInfo ?
          { title: 'User Create', text: 'User created successfully. Registration email sent to the new user' } :
          { title: 'User Update', text: 'User updated successfully' };
        Swal.fire({
          title: messages.title,
          text: messages.text,
          icon: 'success',
          allowOutsideClick: false,
        }).then((result) => {
          this.onClose.next(res);
          this.bsModalRef.hide();
        });
      }, err => Utils.showError(err));

  }

  checkUserEmail($event) {
    const username = $event.target.value;
    if (username) {
      this.apiService.user.checkDuplicateEmail(username)
        .subscribe(res => {
        }, err => {
          Utils.showError(err);
          this.form['username'].setValue(null);
        });
    }
  }

  unlockAccountModalOpen() {
    Swal.fire({
      title: 'User Account Unlock',
      text: 'Will you unlock this user account?',
      icon: 'question',
      allowOutsideClick: true,
    }).then((result) => {
      if (result.isConfirmed) {
        this.apiService.user.unlockUserAccount(this.userInfo.username).subscribe(res => {
          this.isUserLocked = false;
          Swal.fire({
            title: 'User Account Unlock',
            text: 'This user account unlocked successfully.',
            icon: 'success',
            allowOutsideClick: true,
          }).then((result) => {
            this.onClose.next(res);
            this.bsModalRef.hide();
          });
        }, err => Utils.showError(err));
      }
    });
  }

  togglePasswordFields() {
    this.showPasswordFields = !this.showPasswordFields;
    if (!this.showPasswordFields) {
      // Reset form when hiding
      this.passwordForm.reset();
    }
  }

  cancelPasswordChange() {
    this.showPasswordFields = false;
    this.passwordForm.reset();
  }

  submitPasswordChange() {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    const newPassword = this.passwordForm.value.newPassword;
    this.changeUserPassword(newPassword);
  }

  changeUserPassword(newPassword: string) {
    this.spinnerService.start();
    const passwordData = { password: newPassword };

    this.apiService.user.changePassword(this.userInfo._id, passwordData)
      .pipe(finalize(() => this.spinnerService.stop()))
      .subscribe(
        res => {
          this.showPasswordFields = false;
          this.passwordForm.reset();
          Swal.fire({
            title: 'Password Changed',
            text: 'User password has been changed successfully.',
            icon: 'success',
            allowOutsideClick: false,
          });
        },
        err => Utils.showError(err)
      );
  }

  private setEdit(userInfo) {

    const form = this.detailForm.controls;
    userInfo.updatedAt = this.pipe.transform(userInfo.auth.updatedAt, 'shortDate');
    userInfo.createdAt = this.pipe.transform(userInfo.auth.createdAt, 'shortDate');

    let keyRoles = null;

    Object.keys(form).forEach(key => {
      form[key].setValue(userInfo[key]);
      if (key === 'roles') {
        keyRoles = this.retrieveRoles(userInfo[key]);
      }
      if (key === 'username') {
        form[key].disable();
      }

      // Disable certain fields when user is editing their own profile
      if (this.isOwnProfile && ['roles', 'status', 'userType', 'organization'].includes(key)) {
        form[key].disable();
      }
    });
    form['roles'].setValue(keyRoles);

    this.userInfo = userInfo;
  }

  private retrieveRoles(roles) {
    let returnRoles = null;
    if (roles) {
      let company = this.authService.getCurrentCompany();
      this.returnedCompany = company;
      returnRoles = roles?.find(f => f.company === company)?.groups;
    }
    return returnRoles;
  }
}
